import apiClient from "./apiClient";

export interface CardHolder {
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  nationality: string | null;
  avatarUrl: string | null;
}

export interface EmergencySummary {
  bloodType: string | null;
  criticalAllergies: number;
  activeMedications: number;
}

export interface CardDTO {
  cardNumber: string;
  issuedAt: string;
  validUntil: string;
  cardVersion: number;
  subscriptionTier: "FREE" | "PLUS" | "PREMIUM";
  holder: CardHolder;
  emergencySummary: EmergencySummary | null;
}

export interface WalletItem {
  id: string;
  type: string;
  label: string;
  icon: string;
  available: boolean;
  requiredTier: "FREE" | "PLUS" | "PREMIUM";
  count: number | null;
  status: "complete" | "partial" | "empty" | "locked";
  lastUpdated: string | null;
}

export interface CardQR {
  qrDataUrl: string;
  offlineFhirQrDataUrl: string;
  accessToken: string;
  onlineUrl: string;
}

export interface CardShare {
  id: string;
  token: string;
  expiresAt: string;
  hasPassword: boolean;
  revokedAt: string | null;
  accessCount: number;
  lastAccessAt: string | null;
  createdAt: string;
  shareUrl: string;
}

export const cardService = {
  /** Fetch card data for the authenticated user */
  async getCard(): Promise<CardDTO> {
    const res = await apiClient.get("/card");
    return res.data.data;
  },

  /** Fetch wallet items */
  async getWallet(): Promise<WalletItem[]> {
    const res = await apiClient.get("/card/wallet");
    return res.data.data;
  },

  /**
   * The card's current QR codes. Viewing never invalidates a code: a printed
   * or saved card keeps working until its owner asks for a new one.
   */
  async getQR(): Promise<CardQR> {
    const res = await apiClient.get("/card/qr");
    return res.data.data;
  },

  /** A new code; the previous one stops working (e.g. after a lost card). */
  async generateQR(): Promise<CardQR> {
    const res = await apiClient.post("/card/qr/generate");
    return res.data.data;
  },

  /** Create a time-limited share link */
  async createShare(
    expiresIn: "24h" | "72h" | "7d",
    password?: string,
  ): Promise<CardShare> {
    const res = await apiClient.post("/card/shares", { expiresIn, password });
    return res.data.data;
  },

  /** List all share links */
  async getShares(): Promise<CardShare[]> {
    const res = await apiClient.get("/card/shares");
    return res.data.data;
  },

  /** Revoke a share link */
  async revokeShare(id: string): Promise<void> {
    await apiClient.delete(`/card/shares/${id}`);
  },

  /**
   * Access a public share link (no auth). A POST so the password travels in
   * the body: as a query parameter it ended up in server and proxy logs and
   * in the browser history.
   */
  async accessShare(
    token: string,
    password?: string,
  ): Promise<{ cardData: Partial<CardDTO>; shareId: string }> {
    const res = await apiClient.post(
      `/card/share/${encodeURIComponent(token)}`,
      password ? { password } : {},
    );
    return res.data.data;
  },
};
